import UserService from "rc-app/services/UserService";
import React, { useEffect } from "react";
import LandingNav from "rc-app/components/LandingNav";

export const PrivacyPolicy = () => {
  // index.html sets body { overflow: hidden } globally for the authenticated app.
  // Marketing/legal pages need the body to scroll — restore it on mount.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "auto";
    return () => { document.body.style.overflow = prev; };
  }, []);

  return (
    <>
      {!UserService.isLoggedIn() && (
        <>
          <LandingNav />
        <div className="flex-grow pt-[6rem] md:pt-[6rem] lg:pt-[6rem] px-2 lg:px-3">
          <div className="max-w-6xl mx-auto px-4 py-8 font-sans text-gray-800">
            <header className="mb-8">
              <h1 className="text-3xl font-bold text-center mb-2">
                Privacy Policy
              </h1>
              {/* <p className="text-center text-gray-600">
                Last updated: {new Date().toLocaleDateString()}
              </p> */}
            </header>

            <div className="bg-white shadow-md rounded-lg p-6 mb-8 text-justify">
              <p className="mb-4">
                This Privacy Policy describes how OpenSky VR RE Platform (the
                "Site", "we", "us", or "our") collects, uses, and discloses your
                personal information when you visit, use our services, or make a
                purchase from openskyvr.com (the "Site") or otherwise
                communicate with us regarding the Site (collectively, the
                "Services"). For purposes of this Privacy Policy, "you" and
                "your" means you as the user of the Services, whether you are a
                customer, website visitor, or another individual whose
                information we have collected pursuant to this Privacy Policy.
                Please read this Privacy Policy carefully.
              </p>
            </div>

            <div className="space-y-8">
              {/* Changes to This Privacy Policy */}
              <section className="bg-white shadow-md rounded-lg p-6 text-justify">
                <h2 className="text-2xl font-semibold mb-4">
                  Changes to This Privacy Policy
                </h2>
                <p className="mb-4">
                  We may update this Privacy Policy from time to time, including
                  to reflect changes to our practices or for other operational,
                  legal, or regulatory reasons. We will post the revised Privacy
                  Policy on the Site, update the "Last updated" date and take
                  any other steps required by applicable law.
                </p>
              </section>

              {/* How We Collect and Use Your Personal Information */}
              <section className="bg-white shadow-md rounded-lg p-6 text-justify">
                <h2 className="text-2xl font-semibold mb-4">
                  How We Collect and Use Your Personal Information
                </h2>
                <p className="mb-4">
                  To provide the Services, we collect personal information about
                  you from a variety of sources, as set out below. The
                  information that we collect and use varies depending on how
                  you interact with us.
                </p>
                <p className="mb-4">
                  In addition to the specific uses set out below, we may use
                  information we collect about you to communicate with you,
                  provide or improve or improve the Services, comply with any
                  applicable legal obligations, enforce any applicable terms of
                  service, and to protect or defend the Services, our rights,
                  and the rights of our users or others.
                </p>
              </section>

              {/* What Personal Information We Collect */}
              <section className="bg-white shadow-md rounded-lg p-6 text-justify">
                <h2 className="text-2xl font-semibold mb-4">
                  What Personal Information We Collect
                </h2>
                <p className="mb-4">
                  The types of personal information we obtain about you depends
                  on how you interact with our Site and use our Services. When
                  we use the term "personal information", we are referring to
                  information that identifies, relates to, describes or can be
                  associated with you. The following sections describe the
                  categories and specific types of personal information we
                  collect.
                </p>

                <h3 className="text-xl font-medium mb-2 mt-6">
                  Information We Collect Directly from You
                </h3>
                <p className="mb-4">
                  Information that you directly submit to us through our
                  Services may include:
                </p>
                <ul className="list-disc pl-5 mb-4 space-y-1">
                  <li>
                    Contact details including your name, address, phone number,
                    and email.
                  </li>
                  <li>
                    Order information including your name, billing address,
                    shipping address, payment confirmation, email address, and
                    phone number.
                  </li>
                  <li>
                    Account information including your username, password,
                    security questions and other information used for account
                    security purposes.
                  </li>
                  <li>
                    Customer support information including the information you
                    choose to include in communications with us.
                  </li>
                </ul>
                <p className="mb-4">
                  Some features of the Services may require you to directly
                  provide us with certain information about yourself. You may
                  elect not to provide this information, but doing so may
                  prevent you from using or accessing these features.
                </p>

                <h3 className="text-xl font-medium mb-2 mt-6">
                  Information We Collect about Your Usage
                </h3>
                <p className="mb-4">
                  We may also automatically collect certain information about
                  your interaction with the Services ("Usage Data"). To do this,
                  we may use cookies, pixels and similar technologies
                  ("Cookies"). Usage Data may include information about how you
                  access and use our Site and your account, including device
                  information, browser information, information about your
                  network connection, your IP address and other information
                  regarding your interaction with the Services.
                </p>

                <h3 className="text-xl font-medium mb-2 mt-6">
                  Information We Obtain from Third Parties
                </h3>
                <p className="mb-4">
                  Finally, we may obtain information about you from third
                  parties, including from vendors and service providers who may
                  collect information on our behalf, such as:
                </p>
                <ul className="list-disc pl-5 mb-4 space-y-1">
                  <li>
                    Companies who support our Site and Services, such as
                    Shopify.
                  </li>
                  <li>
                    Our payment processors, who collect payment information to
                    process your payment.
                  </li>
                  <li>
                    When you visit our Site, open or click on emails we send
                    you, or interact with our Services or advertisements.
                  </li>
                </ul>
                <p className="mb-4">
                  Any information we obtain from third parties will be treated
                  in accordance with this Privacy Policy. Also see the section
                  below, Third Party Websites and Links.
                </p>
              </section>

              {/* How We Use Your Personal Information */}
              <section className="bg-white shadow-md rounded-lg p-6 text-justify">
                <h2 className="text-2xl font-semibold mb-4">
                  How We Use Your Personal Information
                </h2>
                <ul className="list-disc pl-5 space-y-4">
                  <li>
                    <span className="font-medium">
                      Providing Products and Services:
                    </span>{" "}
                    We use your personal information to provide you with the
                    Services in order to perform our contract with you,
                    including to process your payments, fulfill your orders, to
                    send notifications to you related to your account,
                    purchases, returns, exchanges or other transactions.
                  </li>
                  <li>
                    <span className="font-medium">
                      Marketing and Advertising:
                    </span>{" "}
                    We may use your personal information for marketing and
                    promotional purposes, such as to send marketing, advertising
                    and promotional communications by email, text message or
                    postal mail.
                  </li>
                  <li>
                    <span className="font-medium">
                      Security and Fraud Prevention:
                    </span>{" "}
                    We use your personal information to detect, investigate or
                    take action regarding possible fraudulent, illegal or
                    malicious activity.
                  </li>
                  <li>
                    <span className="font-medium">
                      Communicating with You and Service Improvement:
                    </span>{" "}
                    We use your personal information to provide you with
                    customer support and improve our Services.
                  </li>
                </ul>
              </section>

              {/* Cookies */}
              <section className="bg-white shadow-md rounded-lg p-6 text-justify">
                <h2 className="text-2xl font-semibold mb-4">Cookies</h2>
                <p className="mb-4">
                  Like many websites, we use Cookies on our Site. We use Cookies
                  to power and improve our Site and our Services (including to
                  remember your actions and preferences), to run analytics and
                  better understand user interaction with the Services.
                </p>
                <p className="mb-4">
                  Most browsers automatically accept Cookies by default, but you
                  can choose to set your browser to remove or reject Cookies
                  through your browser controls. Please keep in mind that
                  removing or blocking Cookies can negatively impact your user
                  experience.
                </p>
              </section>

              {/* How We Disclose Personal Information */}
              <section className="bg-white shadow-md rounded-lg p-6 text-justify">
                <h2 className="text-2xl font-semibold mb-4">
                  How We Disclose Personal Information
                </h2>
                <p className="mb-4">
                  In certain circumstances, we may disclose your personal
                  information to third parties for contract fulfillment
                  purposes, legitimate purposes and other reasons subject to
                  this Privacy Policy. Such circumstances may include:
                </p>
                <ul className="list-disc pl-5 mb-4 space-y-1">
                  <li>
                    With vendors or other third parties who perform services on
                    our behalf
                  </li>
                  <li>
                    With business and marketing partners to provide services and
                    advertise to you
                  </li>
                  <li>
                    When you direct, request us or otherwise consent to our
                    disclosure
                  </li>
                  <li>
                    With our affiliates or otherwise within our corporate group
                  </li>
                  <li>
                    In connection with a business transaction such as a merger
                    or bankruptcy
                  </li>
                </ul>
              </section>

              {/* Children's Data */}
              <section className="bg-white shadow-md rounded-lg p-6 text-justify">
                <h2 className="text-2xl font-semibold mb-4">Children's Data</h2>
                <p className="mb-4">
                  The Services are not intended to be used by children, and we
                  do not knowingly collect any personal information about
                  children. If you are the parent or guardian of a child who has
                  provided us with their personal information, you may contact
                  us using the contact details set out below to request that it
                  be deleted.
                </p>
              </section>

              {/* Your Rights */}
              <section className="bg-white shadow-md rounded-lg p-6 text-justify">
                <h2 className="text-2xl font-semibold mb-4">Your Rights</h2>
                <p className="mb-4">
                  Depending on where you live, you may have some or all of the
                  rights listed below in relation to your personal information.
                  However, these rights are not absolute, may apply only in
                  certain circumstances and, in certain cases, we may decline
                  your request as permitted by law.
                </p>
                <ul className="list-disc pl-5 space-y-2">
                  <li>
                    <span className="font-medium">Right to Access / Know:</span>{" "}
                    You may have a right to request access to personal
                    information that we hold about you.
                  </li>
                  <li>
                    <span className="font-medium">Right to Delete:</span> You
                    may have a right to request that we delete personal
                    information we maintain about you.
                  </li>
                  <li>
                    <span className="font-medium">Right to Correct:</span> You
                    may have a right to request that we correct inaccurate
                    personal information.
                  </li>
                  <li>
                    <span className="font-medium">Right of Portability:</span>{" "}
                    You may have a right to receive a copy of the personal
                    information we hold about you.
                  </li>
                  <li>
                    <span className="font-medium">Right to Opt out:</span> You
                    may have a right to direct us not to "sell" or "share" your
                    personal information.
                  </li>
                </ul>
              </section>

              {/* Contact */}
              <section className="bg-white shadow-md rounded-lg p-6 text-justify">
                <h2 className="text-2xl font-semibold mb-4">Contact</h2>
                <p className="mb-4">
                  Should you have any questions about our privacy practices or
                  this Privacy Policy, or if you would like to exercise any of
                  the rights available to you, please call or email us at
                  <a
                    href="mailto:product@hospinetinfra.com"
                    className="text-blue-600 hover:underline"
                  >
                    {" "}
                    product@hospinetinfra.com
                  </a>{" "}
                  or contact us at Emerald 909, My Home Jewel, Madeenaguda,
                  HYDERABAD, TS, 500049, IN.
                </p>
                <p>
                  For the purpose of applicable data protection laws and if not
                  explicitly stated otherwise, we are the data controller of
                  your personal information.
                </p>
              </section>
            </div>
          </div>
        </div>
        </>
      )}

      {UserService.isLoggedIn() && (
        <div className="max-w-6xl mx-auto px-4 py-8 font-sans text-gray-800">
          <header className="mb-8">
            <h1 className="text-3xl font-bold text-center mb-2">
              Privacy Policy
            </h1>
            {/* <p className="text-center text-gray-600">
                Last updated: {new Date().toLocaleDateString()}
              </p> */}
          </header>

          <div className="bg-white shadow-md rounded-lg p-6 mb-8 text-justify">
            <p className="mb-4">
              This Privacy Policy describes how OpenSky VR RE Platform (the
              "Site", "we", "us", or "our") collects, uses, and discloses your
              personal information when you visit, use our services, or make a
              purchase from openskyvr.com (the "Site") or otherwise communicate
              with us regarding the Site (collectively, the "Services"). For
              purposes of this Privacy Policy, "you" and "your" means you as the
              user of the Services, whether you are a customer, website visitor,
              or another individual whose information we have collected pursuant
              to this Privacy Policy. Please read this Privacy Policy carefully.
            </p>
          </div>

          <div className="space-y-8">
            {/* Changes to This Privacy Policy */}
            <section className="bg-white shadow-md rounded-lg p-6 text-justify">
              <h2 className="text-2xl font-semibold mb-4">
                Changes to This Privacy Policy
              </h2>
              <p className="mb-4">
                We may update this Privacy Policy from time to time, including
                to reflect changes to our practices or for other operational,
                legal, or regulatory reasons. We will post the revised Privacy
                Policy on the Site, update the "Last updated" date and take any
                other steps required by applicable law.
              </p>
            </section>

            {/* How We Collect and Use Your Personal Information */}
            <section className="bg-white shadow-md rounded-lg p-6 text-justify">
              <h2 className="text-2xl font-semibold mb-4">
                How We Collect and Use Your Personal Information
              </h2>
              <p className="mb-4">
                To provide the Services, we collect personal information about
                you from a variety of sources, as set out below. The information
                that we collect and use varies depending on how you interact
                with us.
              </p>
              <p className="mb-4">
                In addition to the specific uses set out below, we may use
                information we collect about you to communicate with you,
                provide or improve or improve the Services, comply with any
                applicable legal obligations, enforce any applicable terms of
                service, and to protect or defend the Services, our rights, and
                the rights of our users or others.
              </p>
            </section>

            {/* What Personal Information We Collect */}
            <section className="bg-white shadow-md rounded-lg p-6 text-justify">
              <h2 className="text-2xl font-semibold mb-4">
                What Personal Information We Collect
              </h2>
              <p className="mb-4">
                The types of personal information we obtain about you depends on
                how you interact with our Site and use our Services. When we use
                the term "personal information", we are referring to information
                that identifies, relates to, describes or can be associated with
                you. The following sections describe the categories and specific
                types of personal information we collect.
              </p>

              <h3 className="text-xl font-medium mb-2 mt-6">
                Information We Collect Directly from You
              </h3>
              <p className="mb-4">
                Information that you directly submit to us through our Services
                may include:
              </p>
              <ul className="list-disc pl-5 mb-4 space-y-1">
                <li>
                  Contact details including your name, address, phone number,
                  and email.
                </li>
                <li>
                  Order information including your name, billing address,
                  shipping address, payment confirmation, email address, and
                  phone number.
                </li>
                <li>
                  Account information including your username, password,
                  security questions and other information used for account
                  security purposes.
                </li>
                <li>
                  Customer support information including the information you
                  choose to include in communications with us.
                </li>
              </ul>
              <p className="mb-4">
                Some features of the Services may require you to directly
                provide us with certain information about yourself. You may
                elect not to provide this information, but doing so may prevent
                you from using or accessing these features.
              </p>

              <h3 className="text-xl font-medium mb-2 mt-6">
                Information We Collect about Your Usage
              </h3>
              <p className="mb-4">
                We may also automatically collect certain information about your
                interaction with the Services ("Usage Data"). To do this, we may
                use cookies, pixels and similar technologies ("Cookies"). Usage
                Data may include information about how you access and use our
                Site and your account, including device information, browser
                information, information about your network connection, your IP
                address and other information regarding your interaction with
                the Services.
              </p>

              <h3 className="text-xl font-medium mb-2 mt-6">
                Information We Obtain from Third Parties
              </h3>
              <p className="mb-4">
                Finally, we may obtain information about you from third parties,
                including from vendors and service providers who may collect
                information on our behalf, such as:
              </p>
              <ul className="list-disc pl-5 mb-4 space-y-1">
                <li>
                  Companies who support our Site and Services, such as Shopify.
                </li>
                <li>
                  Our payment processors, who collect payment information to
                  process your payment.
                </li>
                <li>
                  When you visit our Site, open or click on emails we send you,
                  or interact with our Services or advertisements.
                </li>
              </ul>
              <p className="mb-4">
                Any information we obtain from third parties will be treated in
                accordance with this Privacy Policy. Also see the section below,
                Third Party Websites and Links.
              </p>
            </section>

            {/* How We Use Your Personal Information */}
            <section className="bg-white shadow-md rounded-lg p-6 text-justify">
              <h2 className="text-2xl font-semibold mb-4">
                How We Use Your Personal Information
              </h2>
              <ul className="list-disc pl-5 space-y-4">
                <li>
                  <span className="font-medium">
                    Providing Products and Services:
                  </span>{" "}
                  We use your personal information to provide you with the
                  Services in order to perform our contract with you, including
                  to process your payments, fulfill your orders, to send
                  notifications to you related to your account, purchases,
                  returns, exchanges or other transactions.
                </li>
                <li>
                  <span className="font-medium">
                    Marketing and Advertising:
                  </span>{" "}
                  We may use your personal information for marketing and
                  promotional purposes, such as to send marketing, advertising
                  and promotional communications by email, text message or
                  postal mail.
                </li>
                <li>
                  <span className="font-medium">
                    Security and Fraud Prevention:
                  </span>{" "}
                  We use your personal information to detect, investigate or
                  take action regarding possible fraudulent, illegal or
                  malicious activity.
                </li>
                <li>
                  <span className="font-medium">
                    Communicating with You and Service Improvement:
                  </span>{" "}
                  We use your personal information to provide you with customer
                  support and improve our Services.
                </li>
              </ul>
            </section>

            {/* Cookies */}
            <section className="bg-white shadow-md rounded-lg p-6 text-justify">
              <h2 className="text-2xl font-semibold mb-4">Cookies</h2>
              <p className="mb-4">
                Like many websites, we use Cookies on our Site. We use Cookies
                to power and improve our Site and our Services (including to
                remember your actions and preferences), to run analytics and
                better understand user interaction with the Services.
              </p>
              <p className="mb-4">
                Most browsers automatically accept Cookies by default, but you
                can choose to set your browser to remove or reject Cookies
                through your browser controls. Please keep in mind that removing
                or blocking Cookies can negatively impact your user experience.
              </p>
            </section>

            {/* How We Disclose Personal Information */}
            <section className="bg-white shadow-md rounded-lg p-6 text-justify">
              <h2 className="text-2xl font-semibold mb-4">
                How We Disclose Personal Information
              </h2>
              <p className="mb-4">
                In certain circumstances, we may disclose your personal
                information to third parties for contract fulfillment purposes,
                legitimate purposes and other reasons subject to this Privacy
                Policy. Such circumstances may include:
              </p>
              <ul className="list-disc pl-5 mb-4 space-y-1">
                <li>
                  With vendors or other third parties who perform services on
                  our behalf
                </li>
                <li>
                  With business and marketing partners to provide services and
                  advertise to you
                </li>
                <li>
                  When you direct, request us or otherwise consent to our
                  disclosure
                </li>
                <li>
                  With our affiliates or otherwise within our corporate group
                </li>
                <li>
                  In connection with a business transaction such as a merger or
                  bankruptcy
                </li>
              </ul>
            </section>

            {/* Children's Data */}
            <section className="bg-white shadow-md rounded-lg p-6 text-justify">
              <h2 className="text-2xl font-semibold mb-4">Children's Data</h2>
              <p className="mb-4">
                The Services are not intended to be used by children, and we do
                not knowingly collect any personal information about children.
                If you are the parent or guardian of a child who has provided us
                with their personal information, you may contact us using the
                contact details set out below to request that it be deleted.
              </p>
            </section>

            {/* Your Rights */}
            <section className="bg-white shadow-md rounded-lg p-6 text-justify">
              <h2 className="text-2xl font-semibold mb-4">Your Rights</h2>
              <p className="mb-4">
                Depending on where you live, you may have some or all of the
                rights listed below in relation to your personal information.
                However, these rights are not absolute, may apply only in
                certain circumstances and, in certain cases, we may decline your
                request as permitted by law.
              </p>
              <ul className="list-disc pl-5 space-y-2">
                <li>
                  <span className="font-medium">Right to Access / Know:</span>{" "}
                  You may have a right to request access to personal information
                  that we hold about you.
                </li>
                <li>
                  <span className="font-medium">Right to Delete:</span> You may
                  have a right to request that we delete personal information we
                  maintain about you.
                </li>
                <li>
                  <span className="font-medium">Right to Correct:</span> You may
                  have a right to request that we correct inaccurate personal
                  information.
                </li>
                <li>
                  <span className="font-medium">Right of Portability:</span> You
                  may have a right to receive a copy of the personal information
                  we hold about you.
                </li>
                <li>
                  <span className="font-medium">Right to Opt out:</span> You may
                  have a right to direct us not to "sell" or "share" your
                  personal information.
                </li>
              </ul>
            </section>

            {/* Contact */}
            <section className="bg-white shadow-md rounded-lg p-6 text-justify">
              <h2 className="text-2xl font-semibold mb-4">Contact</h2>
              <p className="mb-4">
                Should you have any questions about our privacy practices or
                this Privacy Policy, or if you would like to exercise any of the
                rights available to you, please call or email us at
                <a
                  href="mailto:product@hospinetinfra.com"
                  className="text-blue-600 hover:underline"
                >
                  {" "}
                  product@hospinetinfra.com
                </a>{" "}
                or contact us at Emerald 909, My Home Jewel, Madeenaguda,
                HYDERABAD, TS, 500049, IN.
              </p>
              <p>
                For the purpose of applicable data protection laws and if not
                explicitly stated otherwise, we are the data controller of your
                personal information.
              </p>
            </section>
          </div>
        </div>
      )}
    </>
  );
};
